import { AxiosError } from 'axios';
import { RootState } from 'store/store.types';
import { SearchError } from 'gql/autogen/SearchError';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { InternalContext } from 'gql/autogen/InternalContext';
import { SearchSubErrorCode } from 'gql/autogen/SearchSubErrorCode';
import { SearchErrorCode } from 'gql/autogen/SearchErrorCode';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { SearchResponse } from 'gql/autogen/SearchResponse';
import { DetailsResponse } from 'gql/autogen/DetailsResponse';
import { AvailabilityResponse } from 'gql/autogen/AvailabilityResponse';
import { CalendarResponse } from 'gql/autogen/CalendarResponse';
import { ReviewResponse } from 'gql/autogen/ReviewResponse';
import { TopDestination, TopDestinationsGroup } from 'core/type/ActivitiesTopDestinations.types';
import { LoyaltySearchType } from 'core/type/LoyaltySearch.types';
import { NearBySuggestion } from 'core/type/PlaceSuggestion';
import { RangeFilterEntry } from 'gql/autogen/RangeFilterEntry';
import { ValueFilterEntry } from 'gql/autogen/ValueFilterEntry';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';
import { ValueFilterType } from 'gql/autogen/ValueFilterType';
import { SearchType } from 'gql/autogen/SearchType';
import { Sort } from 'gql/autogen/Sort';

export interface ActivitiesGqlRequestPayloadValidationStatus {
    data: Partial<Record<ActivityGqlOperation, { isSuccess: boolean }>>;
    error: GqlBodyProps & {
        operation: ActivityGqlOperation;
        data: Required<GqlResponseError>;
        status: number;
    };
}

export type ActionCreatorWithGqlApiResponseType =
    | ActionCreatorWithPayload<GqlApiResponse<SearchResponse, SearchRequestProps>>
    | ActionCreatorWithPayload<GqlApiResponse<DetailsResponse, DetailsRequestProps>>
    | ActionCreatorWithPayload<GqlApiResponse<AvailabilityResponse, AvailabilityRequestProps>>
    | ActionCreatorWithPayload<GqlApiResponse<CalendarResponse, CalendarRequestProps>>
    | ActionCreatorWithPayload<GqlApiResponse<ReviewResponse, ReviewRequestProps>>;

export interface GenericGqlResponse {
    isSuccess?: boolean;
    errors?: SearchError[];
    result?: {
        isCompleted?: boolean;
    };
}

export interface GqlResponseError {
    shouldRetry: boolean;
    message: string;
    status: number;
    errorCode?: SearchErrorCode;
    subErrorCode?: SearchSubErrorCode;
}

export enum ActivityGqlOperation {
    search = 'search',
    details = 'details',
    availability = 'availability',
    calendar = 'calendar',
    review = 'review',
}

export enum OperationKey {
    HomePageTopActivitiesAroundYou = 'HomePageTopActivitiesAroundYou',
    PopularActivitiesCarousel = 'PopularActivitiesCarousel',
    DetailPageExploringActivitiesCarousel = 'DetailPageExploringActivitiesCarousel',
    ActivitiesPOI = 'ActivitiesPOI',
    EmptyStateSuggestion = 'EmptyStateSuggestion',
    RecentReviews = 'RecentReviews',
    City = 'City',
    Search = 'Search',
    GlobalKeywordSearch = 'GlobalKeywordSearch',
}

export interface ResponseWithData<T> {
    data: T;
    operation: ActivityGqlOperation;
}

export enum SearchRequestType {
    DEFAULT = 'DEFAULT',
    POPULAR_ACTIVITIES = 'POPULAR_ACTIVITIES',
    IN_CITY_SEARCH = 'IN_CITY_SEARCH',
    POINT_OF_INTEREST = 'POINT_OF_INTEREST',
    GLOBAL_SEARCH = 'GLOBAL_SEARCH',
}

interface CommonSearchRequestProps {
    searchType: SearchType;
    searchRequestType: SearchRequestType;
    cityId?: number;
    keyword?: string;
    defaultSort: Sort;
}

interface PaginationRequestProps {
    pagination: {
        size: number;
        number: number;
    };
}

export interface DetailsPageProps {
    isExploringActivities?: boolean;
    filterActivityId?: string;
    filterType?: ValueFilterType;
    filterCategory?: string;
}

export type SearchRequestProps = PaginationRequestProps & DetailsPageProps & CommonSearchRequestProps;

export interface AvailabilityRequestProps {
    activityToken: string;
    excludePassengers?: boolean;
    enableOfferFilter?: boolean;
    enableOffersGrouping?: boolean;
}

export interface CalendarRequestProps {
    activityToken: string;
    startDate?: string;
    endDate?: string;
    isAdditionalCalendar?: boolean;
}

export interface DetailsRequestProps {
    activityId: number;
    loyaltySearchType: LoyaltySearchType;
}

export interface SortRequestProps {
    sort: {
        code: SortCode;
        order: SortOrder;
    };
}

export interface FilterRequestProps {
    filters: {
        valueFilters: ValueFilterEntry[];
        rangeFilters: RangeFilterEntry[];
    };
}

export interface ReviewRequestProps extends Partial<PaginationRequestProps>, Partial<SortRequestProps>, Partial<FilterRequestProps> {
    activityId: number;
}

export type ActivitiesGqlResponseType = ResponseWithData<Record<ActivityGqlOperation, GenericGqlResponse>>;

export type ActivityGqlQueryArgsType = SearchRequestProps | DetailsRequestProps | AvailabilityRequestProps | CalendarRequestProps | ReviewRequestProps;

export interface ActivityGqlQueryArgs<T> {
    props: T;
    state?: RootState;
    customStateHandling?: boolean;
}

export type GqlQueryAndVariablesBuilder = (state: RootState, props: ActivityGqlQueryArgsType | undefined, context: InternalContext) => GqlBodyProps;

export type GqlLogTags = Record<string, string> & { operation: ActivityGqlOperation; cityId: string; countryId: string; countryISO2: string };

export interface GqlBodyProps {
    pageTypeId: number;
    query: string;
    queryString: string;
    variables: Record<string, unknown>;
    logTags: GqlLogTags;
}

export interface SerializedError {
    name?: string;
    message?: string;
    stack?: string;
    code?: string;
    error?: string;
    status?: number;
}

export interface GqlApiError extends Error, SearchError {
    operation: ActivityGqlOperation;
    /** error message */
    data: string | GqlResponseError;
    /** error message in case of routing related error */
    message: string;
    /** HTTP error code */
    status: number;
    isUnhandledError: boolean;
}

export interface GqlApiErrorWithResponseInfo extends GqlApiError {
    correlationId: string;
    requestHeaders?: Headers;
    responseHeaders?: Headers;

    shouldRetry: string;

    /** retry information * */
    retryAttempt: string;
    requestId: string;
    retryCount: string;
}

export interface UseQueryStateBaseResultWithoutError<T> {
    /** Query has not started yet. */
    isUninitialized: boolean;
    /** Query is currently loading for the first time. No data yet. */
    isLoading: boolean;
    /** Query is currently fetching, but might have data from an earlier request. */
    isFetching: boolean;
    /** Query has data from a successful load. */
    isSuccess: boolean;
    /** Query is currently in "error" state. */
    isError: boolean;
    data?: T;
}

export interface ApiBaseState {
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    error?: GqlApiError;
}

export interface GqlApiResponse<T, V> {
    result: T;
    baseState: ApiBaseState;
    pageTypeId: number;
    config: BootstrapConfig;
    props: V;
    state: RootState;
}

export interface UseQueryStateBaseResult<T> extends UseQueryStateBaseResultWithoutError<T> {
    error?: GqlApiError;
}

export interface QueryArgTypeRoot<T extends GenericGqlResponse | undefined, P extends ActivityGqlQueryArgsType> {
    args: ActivityGqlQueryArgs<P>;
    operation: ActivityGqlOperation;
    key?: string;
    onSuccess?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<P>, result?: T) => void;
    onError?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<P>, error?: GqlApiError, axiosError?: AxiosError) => void;
}

interface QueryArgTypeSearch extends QueryArgTypeRoot<SearchResponse, SearchRequestProps> {
    operation: ActivityGqlOperation.search;
    args: ActivityGqlQueryArgs<SearchRequestProps>;
    onSuccess?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<SearchRequestProps>, result?: SearchResponse) => void;
    onError?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<SearchRequestProps>, error?: GqlApiError, axiosError?: AxiosError) => void;
}

interface QueryArgTypeDetails extends QueryArgTypeRoot<DetailsResponse, DetailsRequestProps> {
    operation: ActivityGqlOperation.details;
    args: ActivityGqlQueryArgs<DetailsRequestProps>;
    onSuccess?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<DetailsRequestProps>, result?: DetailsResponse) => void;
    onError?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<DetailsRequestProps>, error?: GqlApiError, axiosError?: AxiosError) => void;
}

interface QueryArgTypeCalendar extends QueryArgTypeRoot<CalendarResponse, CalendarRequestProps> {
    operation: ActivityGqlOperation.calendar;
    args: ActivityGqlQueryArgs<CalendarRequestProps>;
    onSuccess?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<CalendarRequestProps>, result?: CalendarResponse) => void;
    onError?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<CalendarRequestProps>, error?: GqlApiError, axiosError?: AxiosError) => void;
}

interface QueryArgTypeAvailability extends QueryArgTypeRoot<AvailabilityResponse, AvailabilityRequestProps> {
    operation: ActivityGqlOperation.availability;
    args: ActivityGqlQueryArgs<AvailabilityRequestProps>;
    onSuccess?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<AvailabilityRequestProps>, result?: AvailabilityResponse) => void;
    onError?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<AvailabilityRequestProps>, error?: GqlApiError, axiosError?: AxiosError) => void;
}

interface QueryArgTypeReview extends QueryArgTypeRoot<ReviewResponse, ReviewRequestProps> {
    operation: ActivityGqlOperation.review;
    args: ActivityGqlQueryArgs<ReviewRequestProps>;
    onSuccess?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<ReviewRequestProps>, result?: ReviewResponse) => void;
    onError?: (operation: ActivityGqlOperation, args: ActivityGqlQueryArgs<ReviewRequestProps>, error?: GqlApiError, axiosError?: AxiosError) => void;
}

export type QueryArgType = QueryArgTypeSearch | QueryArgTypeDetails | QueryArgTypeCalendar | QueryArgTypeAvailability | QueryArgTypeReview;

export interface ActivityGqlConfigurationValueTypeRoot<T extends GenericGqlResponse, P extends ActivityGqlQueryArgsType> {
    operation: ActivityGqlOperation;
    buildRequestPayload: (args: ActivityGqlQueryArgs<P>) => GqlBodyProps;
    handleResponse: (response: ActivitiesGqlResponseType) => T | undefined;
    setResponseInStateFn: () => ActionCreatorWithPayload<GqlApiResponse<T, P>>;
    validateRequestPayload: (payload: GqlBodyProps) => ActivitiesGqlRequestPayloadValidationStatus | undefined;
}

interface ActivityGqlConfigurationValueTypeSearch extends ActivityGqlConfigurationValueTypeRoot<SearchResponse, SearchRequestProps> {
    operation: ActivityGqlOperation.search;
    buildRequestPayload: (args: ActivityGqlQueryArgs<SearchRequestProps>) => GqlBodyProps;
    handleResponse: (response: ActivitiesGqlResponseType) => SearchResponse | undefined;
    setResponseInStateFn: () => ActionCreatorWithPayload<GqlApiResponse<SearchResponse, SearchRequestProps>>;
    validateRequestPayload: (payload: GqlBodyProps) => ActivitiesGqlRequestPayloadValidationStatus | undefined;
}

interface ActivityGqlConfigurationValueTypeDetails extends ActivityGqlConfigurationValueTypeRoot<DetailsResponse, DetailsRequestProps> {
    operation: ActivityGqlOperation.details;
    buildRequestPayload: (args: ActivityGqlQueryArgs<DetailsRequestProps>) => GqlBodyProps;
    handleResponse: (response: ActivitiesGqlResponseType) => DetailsResponse | undefined;
    setResponseInStateFn: () => ActionCreatorWithPayload<GqlApiResponse<DetailsResponse, DetailsRequestProps>>;
    validateRequestPayload: (payload: GqlBodyProps) => ActivitiesGqlRequestPayloadValidationStatus | undefined;
}

interface ActivityGqlConfigurationValueTypeCalendar extends ActivityGqlConfigurationValueTypeRoot<CalendarResponse, CalendarRequestProps> {
    operation: ActivityGqlOperation.calendar;
    buildRequestPayload: (args: ActivityGqlQueryArgs<CalendarRequestProps>) => GqlBodyProps;
    handleResponse: (response: ActivitiesGqlResponseType) => CalendarResponse | undefined;
    setResponseInStateFn: () => ActionCreatorWithPayload<GqlApiResponse<CalendarResponse, CalendarRequestProps>>;
    validateRequestPayload: (payload: GqlBodyProps) => ActivitiesGqlRequestPayloadValidationStatus | undefined;
}

interface ActivityGqlConfigurationValueTypeAvailability extends ActivityGqlConfigurationValueTypeRoot<AvailabilityResponse, AvailabilityRequestProps> {
    operation: ActivityGqlOperation.availability;
    buildRequestPayload: (args: ActivityGqlQueryArgs<AvailabilityRequestProps>) => GqlBodyProps;
    handleResponse: (response: ActivitiesGqlResponseType) => AvailabilityResponse | undefined;
    setResponseInStateFn: () => ActionCreatorWithPayload<GqlApiResponse<AvailabilityResponse, AvailabilityRequestProps>>;
    validateRequestPayload: (payload: GqlBodyProps) => ActivitiesGqlRequestPayloadValidationStatus | undefined;
}

interface ActivityGqlConfigurationValueTypeReview extends ActivityGqlConfigurationValueTypeRoot<ReviewResponse, ReviewRequestProps> {
    operation: ActivityGqlOperation.review;
    buildRequestPayload: (args: ActivityGqlQueryArgs<ReviewRequestProps>) => GqlBodyProps;
    handleResponse: (response: ActivitiesGqlResponseType) => ReviewResponse | undefined;
    setResponseInStateFn: () => ActionCreatorWithPayload<GqlApiResponse<ReviewResponse, ReviewRequestProps>>;
    validateRequestPayload: (payload: GqlBodyProps) => ActivitiesGqlRequestPayloadValidationStatus | undefined;
}

export type ActivityGqlConfigurationValueUnionType =
    | ActivityGqlConfigurationValueTypeSearch
    | ActivityGqlConfigurationValueTypeDetails
    | ActivityGqlConfigurationValueTypeCalendar
    | ActivityGqlConfigurationValueTypeAvailability
    | ActivityGqlConfigurationValueTypeReview;

export type ActivityGqlConfigurationValueType = { [ActivityGqlOperation.search]: ActivityGqlConfigurationValueTypeSearch } & {
    [ActivityGqlOperation.details]: ActivityGqlConfigurationValueTypeDetails;
} & { [ActivityGqlOperation.calendar]: ActivityGqlConfigurationValueTypeCalendar } & { [ActivityGqlOperation.availability]: ActivityGqlConfigurationValueTypeAvailability } & {
    [ActivityGqlOperation.review]: ActivityGqlConfigurationValueTypeReview;
};

export type TopDestinationsResponse = UseQueryStateBaseResult<TopDestinationsGroup>;

export type TopCitiesResponse = UseQueryStateBaseResult<TopDestination[]>;

export type CityInfoByGeoRadiusResponse = UseQueryStateBaseResult<NearBySuggestion>;
